// ============================================================================
// FinanceTrendCard (E2, F-523) — tendance 6 mois : revenus / benefice net
// ============================================================================
// Portage du PoC components/finance/finance-trend-card.tsx : barres groupees en SVG
// maison (aucune dependance). Un benefice NEGATIF descend sous la ligne de base a
// zero — d'ou l'echelle bornee par min(0, net) et non par 0.
// Couleurs : cat-5 (revenus) / cat-3 (benefice net), memes teintes que le graphique
// annuel — une meme grandeur garde sa couleur d'un ecran a l'autre.

import type { TFunction } from 'i18next';
import type { FinanceTrend } from '../../hooks/useFinance';
import { formatCad, formatMonthShort } from '../../pages/finance/format';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800';
const REVENUE_COLOR = 'var(--chart-cat-5)';
const NET_COLOR = 'var(--chart-cat-3)';

export interface FinanceTrendCardProps {
  trend: FinanceTrend;
  locale: string;
  t: TFunction;
}

export function FinanceTrendCard({ trend, locale, t }: FinanceTrendCardProps) {
  const { periods, revenue, net } = trend;
  const n = periods.length;
  if (n === 0) return null;

  const width = 600;
  const height = 160;
  const labelZone = 18;
  const chartH = height - labelZone;
  const top = Math.max(1, ...revenue, ...net, 0);
  const bottom = Math.min(0, ...net);
  const span = top - bottom || 1;
  const y = (v: number) => (1 - (v - bottom) / span) * (chartH - 4) + 2;
  const zeroY = y(0);

  const slot = width / n;
  const barW = Math.min(28, slot * 0.28);

  return (
    <section className={CARD}>
      <h3 className="text-head font-semibold text-slate-800 dark:text-slate-100">
        {t('gest:finance.trend.title')}
      </h3>
      <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
        {t('gest:finance.trend.description')}
      </p>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="mt-3 w-full"
        role="img"
        aria-label={periods
          .map((p, i) => t('gest:finance.trend.aria', {
            month: formatMonthShort(p, locale),
            revenue: formatCad(revenue[i] ?? 0, locale),
            net: formatCad(net[i] ?? 0, locale),
          }))
          .join(' ; ')}
      >
        <line x1={0} x2={width} y1={zeroY} y2={zeroY} strokeWidth={1} className="stroke-slate-200 dark:stroke-slate-700" />
        {periods.map((p, i) => {
          const cx = i * slot + slot / 2;
          const rev = revenue[i] ?? 0;
          const nt = net[i] ?? 0;
          return (
            <g key={p}>
              <rect
                x={cx - barW - 2} width={barW}
                y={Math.min(y(rev), zeroY)} height={Math.max(1, Math.abs(y(rev) - zeroY))}
                rx={3} fill={REVENUE_COLOR}
              />
              <rect
                x={cx + 2} width={barW}
                y={Math.min(y(nt), zeroY)} height={Math.max(1, Math.abs(y(nt) - zeroY))}
                rx={3} fill={NET_COLOR}
              />
              <text x={cx} y={height - 4} textAnchor="middle" className="fill-slate-400 text-chip dark:fill-slate-500">
                {formatMonthShort(p, locale)}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="mt-2 flex items-center gap-4 text-meta text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: REVENUE_COLOR }} />
          {t('gest:finance.series.revenue')}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: NET_COLOR }} />
          {t('gest:finance.series.net')}
        </span>
      </div>
    </section>
  );
}
